import React, { useState, useEffect } from 'react';
import {
  FaCheck, FaUserMd, FaCalendarAlt, FaPhone, FaEnvelope,
  FaVenusMars, FaEdit, FaStethoscope, FaDesktop, FaSortNumericDown, FaListAlt,
} from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useRolePath } from '../context/AppContext';
import {
  getDoctors, createPaymentOrder, verifyPayment,
  getSlotAvailability, getRegisteredPatients, createAppointment, getConsultFee,
  getDoctorHolidays,
} from '../services/api';

const DEFAULT_SLOTS = {
  Online:  ['09:00','09:30','10:00','10:30','17:00','17:30','18:00','18:30'],
  Offline: ['10:00','10:30','11:00','11:30','12:00','14:00','14:30','15:00','15:30','16:00'],
};

const loadRazorpay = () =>
  new Promise(resolve => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const formatTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const NewAppointment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, user } = useApp();
  const rp       = useRolePath();
  const role = (user?.role || '').toLowerCase();
  const isStaff = role === 'doctor' || role === 'admin';
  const today = new Date().toISOString().split('T')[0];

  const [agreed, setAgreed]             = useState(location.state?.agreed || false);
  const [doctors, setDoctors]           = useState([]);
  const [patients, setPatients]         = useState([]);
  const [paying, setPaying]             = useState(false);
  const [done, setDone]                 = useState(false);
  const [slotCounts, setSlotCounts]     = useState({});
  const [slotCapacity, setSlotCapacity] = useState(1);
  const [consultFee, setConsultFee]     = useState(900);
  const [doctorHolidays, setDoctorHolidays] = useState([]);

  const [form, setForm] = useState({
    department:      '',
    service:         '',
    doctorId:        '',
    date:            '',
    appointmentMode: '',
    time:            '',
    type:            'Consultation',
    name:            user?.name   || '',
    phone:           user?.phone  || '',
    email:           user?.email  || '',
    gender:          user?.gender || '',
    age:             user?.age    || '',
    notes:           '',
    patientUserId:   '',
  });

  useEffect(() => {
    getDoctors().then(r => setDoctors(r.data?.data || [])).catch(() => {});
    if (isStaff) {
      getRegisteredPatients().then(r => setPatients(r.data?.data || [])).catch(() => {});
    }
    if (!isStaff) {
      getConsultFee().then(r => setConsultFee(r.data?.fee || 900)).catch(() => {});
    }
  }, [isStaff]);

  useEffect(() => {
    if (form.doctorId && form.date) {
      getSlotAvailability(form.doctorId, form.date)
        .then(r => { setSlotCounts(r.data?.counts || {}); setSlotCapacity(r.data?.capacity || 1); })
        .catch(() => {});
    } else {
      setSlotCounts({});
    }
  }, [form.doctorId, form.date]);

  // Mirrors the backend's validateAppointmentSlot holiday check — loaded
  // per doctor so the date field can reject Saturday/Sunday/holiday dates
  // before the patient even picks a time slot.
  useEffect(() => {
    if (form.doctorId) {
      getDoctorHolidays(form.doctorId).then(r => setDoctorHolidays(r.data?.data || [])).catch(() => setDoctorHolidays([]));
    } else {
      setDoctorHolidays([]);
    }
  }, [form.doctorId]);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const departments = [...new Set(doctors.map(d => d.specialization).filter(Boolean))].sort();

  const filteredDoctors = form.department
    ? doctors.filter(d => d.specialization === form.department)
    : doctors;

  const selectedDoctor = filteredDoctors.find(d => String(d.id) === String(form.doctorId));

  // Saturday/Sunday (unless the doctor is exempted) or an active one-time/
  // annual holiday for this doctor — same rule the backend enforces.
  const isDateUnavailable = (dateStr) => {
    if (!dateStr) return false;
    const [y, m, d] = dateStr.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    if ((dow === 0 || dow === 6) && !selectedDoctor?.worksWeekends) return true;
    const monthDay = dateStr.slice(5);
    return doctorHolidays.some(h => h.status === 'active' && (h.type === 'annual' ? h.date.slice(5) === monthDay : h.date === dateStr));
  };

  const dateUnavailable = isDateUnavailable(form.date);

  const availableSlots = (() => {
    if (!form.appointmentMode || !selectedDoctor || dateUnavailable) return [];
    const key = form.appointmentMode === 'Online' ? 'onlineSlots' : 'offlineSlots';
    const configured = Array.isArray(selectedDoctor[key]) ? selectedDoctor[key] : [];
    const allSlots = configured.length > 0 ? configured : DEFAULT_SLOTS[form.appointmentMode] || [];
    return allSlots.filter(t => (slotCounts[t] || 0) < slotCapacity);
  })();

  const handlePatientSelect = (userId) => {
    const pat = patients.find(p => String(p.userId) === String(userId));
    if (!pat) { setField('patientUserId', ''); return; }
    setForm(p => ({
      ...p,
      patientUserId: userId,
      name:   pat.name   || '',
      phone:  pat.phone  || '',
      email:  pat.email  || '',
      gender: pat.gender || '',
      age:    pat.age    || '',
    }));
  };

  const canBook = () =>
    form.service && form.doctorId && form.date && !dateUnavailable &&
    form.appointmentMode && form.time && form.name && form.phone;

  const handlePatientBook = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const appointmentData = {
        doctorId:     form.doctorId,
        doctorName:   selectedDoctor?.name || '',
        service:      form.service,
        type:         form.type,
        date:         form.date,
        time:         form.time,
        mode:         form.appointmentMode,
        patientName:  form.name,
        patientPhone: form.phone,
        email:        form.email,
        gender:       form.gender,
        age:          form.age,
        notes:        form.notes,
      };

      const orderRes = await createPaymentOrder({ appointmentData });
      const { orderId, amount, keyId } = orderRes.data.data;

      const loaded = await loadRazorpay();
      if (!loaded) { showToast('Payment gateway failed to load', 'error'); setPaying(false); return; }

      const options = {
        key:         keyId,
        amount,
        currency:    'INR',
        name:        'DocApps Solutions',
        description: `${form.appointmentMode} Consultation — ${form.service}`,
        order_id:    orderId,
        prefill:     { name: form.name, email: form.email, contact: form.phone },
        theme:       { color: '#E8500A' },
        handler: async (response) => {
          try {
            await verifyPayment({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            });
            setDone(true);
            showToast('Appointment booked & payment verified!', 'success');
          } catch {
            showToast('Payment verification failed. Contact support.', 'error');
            setPaying(false);
          }
        },
        modal: { ondismiss: () => { showToast('Payment cancelled', 'error'); setPaying(false); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { showToast('Payment failed. Please retry.', 'error'); setPaying(false); });
      rzp.open();
    } catch (err) {
      showToast(err?.message || 'Failed to initiate payment', 'error');
      setPaying(false);
    }
  };

  const handleStaffBook = async () => {
    if (paying) return;
    setPaying(true);
    try {
      await createAppointment({
        doctorId:     form.doctorId,
        doctorName:   selectedDoctor?.name || '',
        service:      form.service,
        date:         form.date,
        time:         form.time,
        mode:         form.appointmentMode,
        patientName:  form.name,
        patientPhone: form.phone,
        patientId:    form.patientUserId || null,
        type:         form.type || 'Consultation',
        status:       'Scheduled',
        notes:        form.notes,
        approvedBy:   role,
      });
      setDone(true);
      showToast('Appointment booked!', 'success');
    } catch (err) {
      showToast(err?.message || 'Booking failed', 'error');
      setPaying(false);
    }
  };

  const handleBook = () => {
    if (!canBook()) { showToast('Fill all required fields', 'error'); return; }
    if (isStaff) handleStaffBook();
    else handlePatientBook();
  };

  // ── Success screen ───────────────────────────────────────────────────────────
  if (done) return (
    <div className="page-wrapper">
      <Header title="Appointment Booked" />
      <Sidebar />
      <main className="page-content" style={{ display:'flex', alignItems:'center', justifyContent:'center' }}>
        <div style={{ textAlign:'center', padding:32 }}>
          <div className="na-success-circle"><FaCheck size={36} /></div>
          <h2 style={{ fontSize:22, fontWeight:700, marginTop:20 }}>Appointment Booked!</h2>
          <p style={{ color:'var(--text-light)', marginTop:8 }}>
            {isStaff ? 'Appointment created successfully.' : 'Payment verified. Doctor has been notified.'}
          </p>
          <button className="btn btn-primary" style={{ marginTop:24 }} onClick={() => navigate(rp('/appointments'))}>
            View Appointments
          </button>
        </div>
      </main>
      <BottomNav />
      <style>{`.na-success-circle{width:90px;height:90px;background:linear-gradient(135deg,#4CAF50,#43A047);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;margin:0 auto;box-shadow:0 8px 30px rgba(76,175,80,.35);animation:naScaleIn .4s cubic-bezier(.175,.885,.32,1.275)}@keyframes naScaleIn{from{transform:scale(0)}to{transform:scale(1)}}`}</style>
    </div>
  );

  // ── T&C screen (patient navigating directly without state.agreed) ─────────────
  if (!agreed && !isStaff) return (
    <div className="page-wrapper">
      <Header title="Book Appointment" />
      <Sidebar />
      <main className="page-content" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'70vh' }}>
        <div style={{ width:'100%', maxWidth:560, padding:'0 16px' }}>
          <div style={{ background:'white', borderRadius:16, padding:'40px 32px', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize:22, fontWeight:700, color:'#2d3a4b', marginBottom:20 }}>Terms &amp; Conditions</h2>
            <p style={{ fontSize:14, color:'#6b7a8d', lineHeight:1.8, marginBottom:28 }}>
              This payment is valid for <strong>7 days</strong> (including the day of consultation) which includes
              consultation and one review of the investigations advised within that period.
            </p>
            <div style={{ marginBottom:32 }}>
              <div style={{ fontSize:13, color:'#6b7a8d', marginBottom:6 }}>Fees</div>
              <div style={{ fontSize:36, fontWeight:800, color:'#2d3a4b' }}>INR {consultFee}.00</div>
            </div>
            <button
              onClick={() => setAgreed(true)}
              style={{
                width:'100%', padding:'16px 0', borderRadius:50, border:'none',
                background:'linear-gradient(135deg,#E8500A,#FF6B2C)', color:'white', fontSize:16, fontWeight:700,
                cursor:'pointer', letterSpacing:0.5,
              }}
            >
              I Agree
            </button>
          </div>
        </div>
      </main>
      <BottomNav />
    </div>
  );

  // ── Booking form ──────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <Header title="Book Appointment" />
      <Sidebar />
      <main className="page-content">
        <div className="na-form-card">

          {/* Department / Specialty */}
          <div className="na-field-row">
            <FaStethoscope className="na-field-icon" />
            <select
              value={form.department}
              onChange={e => {
                const dept = e.target.value;
                setForm(p => ({ ...p, department: dept, service: dept, doctorId: '', time: '' }));
              }}
              className="na-field-input"
            >
              <option value="">Select Department</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* Doctor (filtered by selected department) */}
          <div className="na-field-row">
            <FaUserMd className="na-field-icon" />
            <select
              value={form.doctorId}
              onChange={e => { setField('doctorId', e.target.value); setField('time', ''); }}
              className="na-field-input"
            >
              <option value="">Select Doctor</option>
              {filteredDoctors.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div className="na-field-row">
            <FaCalendarAlt className="na-field-icon" />
            <input
              type="date" min={today} value={form.date}
              onChange={e => { setField('date', e.target.value); setField('time', ''); }}
              className="na-field-input"
            />
          </div>
          {dateUnavailable && (
            <div style={{ padding: '8px 20px', fontSize: 12, color: '#c62828', background: '#ffebee' }}>
              Doctor is unavailable on this date (holiday/weekend). Please choose another date.
            </div>
          )}

          {/* Appointment Mode */}
          <div className="na-field-row">
            <FaDesktop className="na-field-icon" />
            <select
              value={form.appointmentMode}
              onChange={e => { setField('appointmentMode', e.target.value); setField('time', ''); }}
              className="na-field-input"
            >
              <option value="">Appointment Type</option>
              <option value="Online">ONLINE</option>
              <option value="Offline">OFFLINE</option>
            </select>
          </div>

          {/* Appointment Type */}
          <div className="na-field-row">
            <FaListAlt className="na-field-icon" />
            <select
              value={form.type}
              onChange={e => setField('type', e.target.value)}
              className="na-field-input"
            >
              <option value="Consultation">Consultation</option>
              <option value="Follow-up">Follow-up</option>
              <option value="Emergency">Emergency</option>
              <option value="Routine Check">Routine Check</option>
              <option value="Telemedicine">Telemedicine</option>
            </select>
          </div>

          {/* Available Time Slots — only shows non-full slots */}
          {form.doctorId && form.date && form.appointmentMode && (
            <div className="na-slots-row">
              {availableSlots.length > 0 ? (
                <div className="na-slots-grid">
                  {availableSlots.map(t => (
                    <div
                      key={t}
                      className={`na-slot${form.time === t ? ' na-slot-selected' : ''}`}
                      onClick={() => setField('time', t)}
                    >
                      {formatTime(t)}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize:13, color:'#9ba8b5', margin:0 }}>
                  No available slots for this date &amp; mode.
                </p>
              )}
            </div>
          )}

          {/* Patient name — dropdown for staff, text input for patient */}
          <div className="na-field-row">
            <span className="na-field-icon" style={{ fontSize:16 }}>👤</span>
            {isStaff ? (
              <select
                value={form.patientUserId}
                onChange={e => handlePatientSelect(e.target.value)}
                className="na-field-input"
              >
                <option value="">Select Patient</option>
                {patients.map(p => (
                  <option key={p.userId} value={p.userId}>{p.name}</option>
                ))}
              </select>
            ) : (
              <input
                type="text" value={form.name}
                onChange={e => setField('name', e.target.value)}
                placeholder="Name" className="na-field-input"
              />
            )}
          </div>

          {/* Phone */}
          <div className="na-field-row">
            <FaPhone className="na-field-icon" />
            <input
              type="tel" value={form.phone}
              onChange={e => setField('phone', e.target.value)}
              placeholder="Mobile / Phone" className="na-field-input"
            />
          </div>

          {/* Email */}
          <div className="na-field-row">
            <FaEnvelope className="na-field-icon" />
            <input
              type="email" value={form.email}
              onChange={e => setField('email', e.target.value)}
              placeholder="Email" className="na-field-input"
            />
          </div>

          {/* Gender */}
          <div className="na-field-row">
            <FaVenusMars className="na-field-icon" />
            <select value={form.gender} onChange={e => setField('gender', e.target.value)} className="na-field-input">
              <option value="">Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Age */}
          <div className="na-field-row">
            <FaSortNumericDown className="na-field-icon" />
            <input
              type="number" min="0" max="120" value={form.age}
              onChange={e => setField('age', e.target.value)}
              placeholder="Age" className="na-field-input"
            />
          </div>

          {/* Notes / Describe Issues */}
          <div className="na-field-row" style={{ alignItems:'flex-start', paddingTop:16, paddingBottom:16 }}>
            <FaEdit className="na-field-icon" style={{ marginTop:2 }} />
            <textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              placeholder="Describe Your Issues"
              rows={3}
              className="na-field-input"
              style={{ resize:'none', border:'none', outline:'none', background:'transparent' }}
            />
          </div>

          {/* Book Now — inside form card at bottom */}
          <div style={{ padding: '20px 20px 24px' }}>
            <button
              className="na-book-btn"
              onClick={handleBook}
              disabled={paying}
            >
              {paying ? 'Processing...' : isStaff ? 'Book Now' : `Book Now — Pay ₹${consultFee}`}
            </button>
          </div>

        </div>
      </main>
      <BottomNav />

      <style>{`
        .na-form-card{background:white;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(232,80,10,0.08);margin-bottom:24px}
        .na-field-row{display:flex;align-items:center;gap:14px;padding:0 20px;border-bottom:1px solid #f0e8e4;min-height:54px}
        .na-field-row:last-child{border-bottom:none}
        .na-field-icon{color:#c0887a;flex-shrink:0;font-size:15px}
        .na-field-input{flex:1;border:none;outline:none;font-size:14px;color:#1a1a2e;background:transparent;padding:14px 0;font-family:inherit}
        .na-field-input option{color:#1a1a2e}
        select.na-field-input{cursor:pointer;appearance:none}
        .na-slots-row{padding:14px 20px;border-bottom:1px solid #f0e8e4}
        .na-slots-grid{display:flex;flex-wrap:wrap;gap:8px}
        .na-slot{padding:8px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;background:#E8500A;color:white;transition:all .15s;text-align:center;min-width:84px}
        .na-slot:hover{background:#FF6B2C}
        .na-slot-selected{background:#1a1a2e !important;color:white;box-shadow:0 2px 8px rgba(26,26,46,0.25)}
        .na-book-btn{width:100%;padding:20px 0;border-radius:14px;border:none;background:linear-gradient(135deg,#E8500A,#FF6B2C);color:white;font-size:18px;font-weight:700;cursor:pointer;letter-spacing:.5px;box-shadow:0 6px 24px rgba(232,80,10,0.4);transition:all .2s}
        .na-book-btn:hover{box-shadow:0 8px 28px rgba(232,80,10,0.5);transform:translateY(-1px)}
        .na-book-btn:disabled{opacity:.6;cursor:not-allowed;transform:none;box-shadow:none}
      `}</style>
    </div>
  );
};

export default NewAppointment;
